import React, { Component, ComponentType } from 'react';
import { connect } from 'react-redux';
import { getStaticViewState } from '../redux/staticViews/selectors';
import { RootState } from '../redux/reducers';
import {
  init,
  setSelected,
  setSearch,
  setOrder,
  getRecordDetails,
  refreshData,
  getDataStart,
  saveColumnsUserSetup,
  getDataNextPage,
  syncDataWithCache,
  openFilterCategory,
  closeFilterCategory,
  selectFilterValues,
} from '../redux/staticViews/actions';

type MapDispatchToProps = ReturnType<typeof mapDispatchToProps>;
type MapStateToProps = ReturnType<typeof mapStateToProps>;

export interface WithViewDataSetHandlerProps {
  state: ViewDataSet;
  setSelected: (selected: ViewDataSet['selected']) => void;
  setSearch: (search: ViewDataSet['search']) => void;
  setOrder: (settings: OrderSettings) => void;
  getRecordDetails: (id: string | number) => void;
  refreshData: () => void;
  getDataStart: () => void;
  saveColumnsUserSetup: () => void;
  getDataNextPage: () => void;
  syncDataWithCache: () => void;
  openFilterCategory: (filtersGroups: FilterSettings) => void;
  closeFilterCategory: (filter: string) => void;
  selectFilterValues: (filters: SelectFilterValuesPayload) => void;
}

type ComponentProps = StaticViewComponent;
export type ViewDataSetHandler = ComponentProps & MapStateToProps & MapDispatchToProps;

/**
 * HOC dostarczający akcje reduxowe oraz stan reduxa dla zakładek statycznych.
 * Na montowaniu komponentu dispatchuje akcje inicjalizacji pobierania danych dla zakładki.
 * Jako argument należy podać komponent widoku statycznego (posiadającego props 'view').
 *
 * @param Cmp
 */
export const withViewDataSetHandler = <P extends ComponentProps>(Cmp: ComponentType<P>) => {
  class Controller extends Component<ViewDataSetHandler> {
    componentDidMount(): void {
      if (this.props.state.initTable) {
        this.props.init();
      }
    }
    render(): React.ReactElement {
      const {
        state,
        init,
        setSelected,
        setSearch,
        setOrder,
        getRecordDetails,
        refreshData,
        getDataStart,
        saveColumnsUserSetup,
        getDataNextPage,
        syncDataWithCache,
        openFilterCategory,
        closeFilterCategory,
        selectFilterValues,
        ...rest
      } = this.props;
      return (
        <Cmp
          {...(rest as P)}
          state={state}
          setSelected={setSelected}
          setSearch={setSearch}
          setOrder={setOrder}
          getRecordDetails={getRecordDetails}
          refreshData={refreshData}
          getDataStart={getDataStart}
          saveColumnsUserSetup={saveColumnsUserSetup}
          getDataNextPage={getDataNextPage}
          syncDataWithCache={syncDataWithCache}
          openFilterCategory={openFilterCategory}
          closeFilterCategory={closeFilterCategory}
          selectFilterValues={selectFilterValues}
        />
      );
    }
  }
  return connect(mapStateToProps, mapDispatchToProps)(Controller);
};
const mapStateToProps = (state: RootState, props: ComponentProps) => ({
  state: getStaticViewState(state, props.view),
});

const mapDispatchToProps = (dispatch: any, props: ComponentProps) => ({
  init: () => dispatch(init(props.view)),
  setSelected: (selected: ViewDataSet['selected']) => dispatch(setSelected(props.view, selected)),
  setSearch: (search: ViewDataSet['search']) => dispatch(setSearch(props.view, search)),
  setOrder: (settings: OrderSettings) => dispatch(setOrder(props.view, settings)),
  getRecordDetails: (id: string | number) => dispatch(getRecordDetails(props.view, id)),
  refreshData: () => dispatch(refreshData(props.view)),
  getDataStart: () => dispatch(getDataStart(props.view)),
  saveColumnsUserSetup: (selectedColumns: SelectedColumns) => dispatch(saveColumnsUserSetup(props.view, selectedColumns)),
  getDataNextPage: () => dispatch(getDataNextPage(props.view)),
  syncDataWithCache: () => dispatch(syncDataWithCache(props.view)),
  openFilterCategory: (filtersGroups: FilterSettings) => dispatch(openFilterCategory(props.view, filtersGroups)),
  closeFilterCategory: (filter: string) => dispatch(closeFilterCategory(props.view, filter)),
  selectFilterValues: (filters: SelectFilterValuesPayload) => dispatch(selectFilterValues(props.view, filters)),
});

export default withViewDataSetHandler;
